/**
 * Live updates: the API streams Server-Sent Events when quotes, alerts or positions change (the
 * worker writes them from another process). While the stream is down (or the browser has none),
 * the app polls every minute instead, and only while the tab is visible. When the stream comes
 * back, everything is refreshed once to catch up on what it missed.
 */
import { createEffect, createEvent, createStore, merge, sample } from 'effector';
import { interval } from 'patronum';
import { alertsRefreshed } from '../entities/alert';
import { marketRefreshed } from '../entities/market';
import { positionsRefreshed } from '../entities/position';

export const liveStarted = createEvent();
export const liveStopped = createEvent();

const quotesChanged = createEvent();
const alertsChanged = createEvent();
const positionsChanged = createEvent();
const streamOpened = createEvent();
const streamFailed = createEvent();
const everythingRefreshed = createEvent();

let source: EventSource | null = null;

const connectFx = createEffect(() => {
  source?.close();
  if (typeof EventSource === 'undefined') {
    streamFailed();
    return;
  }
  source = new EventSource('/api/v1/events');
  source.addEventListener('open', () => {
    streamOpened();
  });
  // The browser retries on its own; polling covers the gap.
  source.addEventListener('error', () => {
    streamFailed();
  });
  source.addEventListener('quotes', () => {
    quotesChanged();
  });
  source.addEventListener('alerts', () => {
    alertsChanged();
  });
  source.addEventListener('positions', () => {
    positionsChanged();
  });
});

const disconnectFx = createEffect(() => {
  source?.close();
  source = null;
});

sample({ clock: liveStarted, target: connectFx });
sample({ clock: liveStopped, target: disconnectFx });

sample({ clock: quotesChanged, target: [positionsRefreshed, marketRefreshed] });
sample({ clock: alertsChanged, target: [alertsRefreshed, positionsRefreshed] });
sample({ clock: positionsChanged, target: positionsRefreshed });
sample({
  clock: everythingRefreshed,
  target: [positionsRefreshed, marketRefreshed, alertsRefreshed],
});

const streamRecovered = createEvent();

/** True after the stream failed, until it has opened again and the app caught up. */
const $streamDown = createStore(false)
  .on(streamFailed, () => true)
  .reset(streamRecovered, liveStarted, liveStopped);

// Catch up once on what the stream missed while it was down.
sample({
  clock: streamOpened,
  source: $streamDown,
  filter: Boolean,
  target: [everythingRefreshed, streamRecovered],
});

const { tick } = interval({
  timeout: 60_000,
  start: streamFailed,
  stop: merge([streamOpened, liveStopped]),
});
sample({
  clock: tick,
  filter: () => document.visibilityState === 'visible',
  target: everythingRefreshed,
});
